'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import {
  getVoiceEnabled,
  hasPermission,
  PERMISSIONS_UPDATED_EVENT,
} from '@/lib/permissions';

/**
 * #402 — como a chamada entrante aparece pro atendente.
 *
 * Persistido em `custom_attributes.incoming_call_ui_mode` da conta do motor
 * (endpoint `/telefonia/canal/modo-chamada-entrante`). É configuração do
 * TENANT: vale pra todos os atendentes e não muda durante a sessão.
 */
export type IncomingCallUiMode = 'fullscreen' | 'discreet';

/**
 * Padrão desde 26/08/2026: **discreto**.
 *
 * Era `fullscreen` — "o comportamento de sempre" — enquanto o modo discreto
 * era novidade opcional. Depois do smoke o padrão foi invertido: tomar a tela
 * inteira do atendente é a exceção, não a regra. ⚠️ Consequência que não é
 * detalhe: instalação que NUNCA configurou nada vê a ligação diferente a
 * partir desta versão. Quem quer a tela cheia agora precisa escolher.
 *
 * Este valor tem um gêmeo no backend
 * (`apps/api/src/voice/dto/incoming-call-ui-mode.dto.ts`) — os dois normalizam
 * o mesmo `custom_attributes` e precisam mudar JUNTOS, senão a mesma conta
 * responde uma coisa pelo endpoint e desenha outra na tela.
 */
export const DEFAULT_INCOMING_CALL_UI_MODE: IncomingCallUiMode = 'discreet';

const ENDPOINT = '/telefonia/canal/modo-chamada-entrante';

/**
 * Todo valor que não seja exatamente um dos dois modos vira o default. O
 * backend já normaliza, mas repetir aqui custa nada e cobre resposta de uma
 * versão futura da API que este build não conhece.
 */
export function normalizeIncomingCallUiMode(raw: unknown): IncomingCallUiMode {
  return raw === 'discreet' || raw === 'fullscreen'
    ? raw
    : DEFAULT_INCOMING_CALL_UI_MODE;
}

export async function fetchIncomingCallUiMode(): Promise<IncomingCallUiMode> {
  const data = await api.request<{ mode?: string }>(ENDPOINT);
  return normalizeIncomingCallUiMode(data?.mode);
}

export async function saveIncomingCallUiMode(
  mode: IncomingCallUiMode,
): Promise<void> {
  await api.request(ENDPOINT, {
    method: 'PUT',
    body: JSON.stringify({ mode }),
  });
}

/**
 * Leitura para o WEBPHONE — uma vez por aba, no `WebphoneProvider`.
 *
 * Três gates que não são opcionais:
 *
 * 1. `getVoiceEnabled()`: o `WebphoneProvider` é montado em TODO dashboard,
 *    inclusive em tenant sem telefonia (`voice-shell.tsx` monta o provider
 *    sempre e só o sticky é condicional — trocar o tipo daquela posição
 *    remontaria o app inteiro). Sem este gate, todo tenant sem voz faria um
 *    request que só pode dar erro.
 * 2. `hasPermission('telefonia.dashboard.ver')`: a permissão chega assíncrona
 *    depois do login, e o `PERMISSIONS_UPDATED_EVENT` re-arma a tentativa —
 *    sem isso, quem recebe telefonia depois do mount ficaria no default até o
 *    próximo F5.
 * 3. Falha para o PADRÃO: enquanto carrega, e em QUALQUER erro, o valor é
 *    `DEFAULT_INCOMING_CALL_UI_MODE` — hoje `discreet`. Duas razões pra o
 *    fallback ser o padrão em vez de um terceiro comportamento fixo: motor
 *    fora do ar passa a desenhar a ligação igual a uma instalação que nunca
 *    configurou nada (uma resposta só pra "não sei qual é o modo"), e o modo
 *    padrão é justamente o que menos atrapalha — não sabendo, é melhor não
 *    tomar a tela inteira do atendente. ⚠️ Quem configurou `fullscreen` e cai
 *    aqui vê a ligação discreta durante a indisponibilidade; a alternativa
 *    (fixar `fullscreen` no erro) faria o oposto com quem não configurou nada,
 *    que é a maioria.
 */
export function useIncomingCallUiMode(): IncomingCallUiMode {
  const [mode, setMode] = useState<IncomingCallUiMode>(
    DEFAULT_INCOMING_CALL_UI_MODE,
  );
  // Guarda a leitura única por aba: o efeito pode reavaliar quando as
  // permissões mudam, mas o request só sai uma vez.
  const fetchedRef = useRef(false);
  // Ref de MONTAGEM (não de "esta execução do efeito"): só vira `false` no
  // desmonte de verdade, no cleanup do efeito de baixo — que roda uma vez
  // (`[]`) e não é reavaliado por `permTick`.
  //
  // Correção de uma corrida real (rodada de revisão 2): a versão anterior
  // usava uma variável `cancelled` local, fechada por execução do efeito de
  // fetch (dependente de `[permTick]`). `notifyPermissionsUpdated()` roda em
  // TODA mudança de rota e todo foco de janela — então bastava o agente
  // navegar com o GET em voo pra `PERMISSIONS_UPDATED_EVENT` disparar,
  // `permTick` mudar, o efeito reavaliar, e o cleanup da execução anterior
  // marcar `cancelled = true` — mesmo sem o componente ter desmontado. O
  // resultado (já a caminho) era descartado, e como `fetchedRef.current` já
  // era `true`, nunca mais havia nova tentativa: a aba ficava presa em
  // `fullscreen` pro resto da sessão. Amarrar a checagem à MONTAGEM real, e
  // não à reavaliação do efeito, resolve — um fetch em voo só é descartado
  // se o componente de fato desmontar.
  //
  // ⚠️ Quem REARMA o ref é o efeito abaixo, não o `useRef(true)`. O `useRef` só
  // roda na criação do componente, e sob StrictMode (`next dev` — o
  // `reactStrictMode` não está setado no next.config.ts, e o default no App
  // Router é `true`; o ecosystem.config.js sobe o web com `args: 'dev'`) o
  // React monta, roda a limpeza e remonta REUSANDO a mesma instância: o ref
  // sobrevive zerado e nunca voltaria a `true`. O fetch resolveria com
  // `mountedRef.current === false` e o modo discreto morreria em silêncio, sem
  // segunda chance — `fetchedRef` já está marcado.
  const mountedRef = useRef(true);
  const [permTick, setPermTick] = useState(0);

  useEffect(() => {
    // Primeira linha, sempre: é a montagem VIVA que este ref representa.
    mountedRef.current = true;
    const bump = () => setPermTick((n) => n + 1);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, bump);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, bump);
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    if (!getVoiceEnabled()) return;
    if (!hasPermission('telefonia.dashboard.ver')) return;
    fetchedRef.current = true;

    fetchIncomingCallUiMode()
      .then((next) => {
        if (mountedRef.current) setMode(next);
      })
      .catch(() => {
        // Fica no padrão (gate 3 do docblock), sem ruído no console.
      });
  }, [permTick]);

  return mode;
}

/**
 * Leitura para a TELA DE CONFIGURAÇÃO do canal.
 *
 * Separada de propósito da de cima: aqui não há gate (a tela já é do admin do
 * canal), o `loading` precisa aparecer na UI, e o `refetch` roda depois de
 * salvar — coisas que o hook do webphone não pode ter, porque ele lê uma vez
 * só e some.
 */
export function useIncomingCallUiModeSetting() {
  const [mode, setMode] = useState<IncomingCallUiMode>(
    DEFAULT_INCOMING_CALL_UI_MODE,
  );
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setMode(await fetchIncomingCallUiMode());
    } catch {
      setMode(DEFAULT_INCOMING_CALL_UI_MODE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { mode, loading, refetch };
}
